import { useTranslation } from 'react-i18next';
import type {
  InsightsModel,
  InsightsRateCard,
} from '@/api/assistantInsights/assistantInsightsApi.types';
import { Card } from '@/components/ui/card';
import { formatCompactNumber, formatMicroUsd, formatUsdRate } from './usageFormatting';

interface UsageRateCardProps {
  rateCard: InsightsRateCard;
  models: InsightsModel[];
}

export function UsageRateCard({ rateCard, models }: UsageRateCardProps) {
  const { t, i18n } = useTranslation('assistant');
  const rates: [string, string][] = [
    [t('insights.rateCard.input'), rateCard.inputUsdPerMillion],
    [t('insights.rateCard.cached'), rateCard.cachedInputUsdPerMillion],
    [t('insights.rateCard.cacheWrite'), rateCard.cacheWriteUsdPerMillion],
    [t('insights.rateCard.output'), rateCard.outputUsdPerMillion],
  ];
  return (
    <Card as="section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm font-bold text-zooindigo">{t('insights.rateCard.title')}</h2>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {rates.map(function renderRate([label, value]) {
              return (
                <div
                  key={label}
                  className="rounded-xl bg-zoooffwhite border border-zooborder px-3 py-2"
                >
                  <dt className="text-[11px] text-zoomuted">{label}</dt>
                  <dd className="text-sm font-bold text-zooindigo">
                    {formatUsdRate(value, i18n.language)}
                  </dd>
                </div>
              );
            })}
          </dl>
          <p className="text-[11px] text-zoomuted mt-3">{t('insights.rateCard.note')}</p>
        </div>
        <div>
          <h2 className="text-sm font-bold text-zooindigo">{t('insights.rateCard.models')}</h2>
          {models.length === 0 ? (
            <p className="text-xs text-zoomuted mt-3">{t('insights.trend.empty')}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {models.map(function renderModel(model) {
                return (
                  <li
                    key={model.provider + ':' + model.model}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="text-zooindigo font-medium">
                      {model.model}{' '}
                      <span className="text-xs text-zoomuted">· {model.provider}</span>
                    </span>
                    <span className="text-zoomuted text-xs">
                      {t('insights.kpis.modelCalls', {
                        count: model.modelCalls,
                        formattedCount: formatCompactNumber(model.modelCalls, i18n.language),
                      })}{' '}
                      ·{' '}
                      <span className="font-semibold text-zooindigo">
                        {formatMicroUsd(model.costMicroUsd, i18n.language)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
